const SALES_CHANNEL_PARAM = 'sales_channel'
const SALES_CHANNEL_VALUE = 'venn_apps'
const SALES_CHANNEL_COOKIE = 'venn_apps_webview'

const variantProductMap = new Map()
let variantMapInitialised = false
let cachedWebViewDetection = null

const toArray = (value) => Array.isArray(value) ? value : []

const toGid = (prefix, id) => {
  if (!id && id !== 0) return null
  const idString = String(id)
  if (idString.startsWith('gid://')) return idString
  return `gid://shopify/${prefix}/${idString}`
}

const normaliseProperties = (properties) => {
  if (!properties || typeof properties !== 'object') {
    return undefined
  }

  if (Array.isArray(properties)) {
    return properties.reduce((acc, entry) => {
      if (entry && typeof entry === 'object' && 'name' in entry) {
        acc[entry.name] = entry.value
      }
      return acc
    }, {})
  }

  return { ...properties }
}

const extractHandleFromUrl = (url) => {
  if (!url || typeof url !== 'string') return null
  try {
    const cleaned = url.startsWith('http') ? new URL(url).pathname : url
    const parts = cleaned.replace(/^\/?/, '').split('/')
    const productIndex = parts.findIndex((segment) => segment === 'products')
    if (productIndex >= 0 && parts[productIndex + 1]) {
      return parts[productIndex + 1]
    }
    if (parts.length) {
      return parts[parts.length - 1]
    }
  } catch (error) {
    console.warn('Unable to parse handle from URL', url, error)
  }
  return null
}

const addVariantMapping = (variantId, productId, productHandle) => {
  if (!variantId && variantId !== 0) return

  const key = String(variantId)
  const existing = variantProductMap.get(key) || {}
  const merchandiseId = toGid('ProductVariant', variantId) || existing.merchandiseId
  const productGid = productId ? toGid('Product', productId) : existing.productId

  variantProductMap.set(key, {
    merchandiseId,
    productId: productGid || null,
    productHandle: productHandle || existing.productHandle || null
  })
}

const collectFromProduct = (product = {}) => {
  const productId = product.id || product.productId
  const productHandle = product.handle

  toArray(product.variants).forEach((variant) => {
    const variantId = variant.id ?? variant.variantId
    const variantProductId = variant.productId || variant.product_id || variant.product?.id || productId
    const variantHandle = variant.product?.handle || productHandle
    addVariantMapping(variantId, variantProductId, variantHandle)
  })
}

const collectFromVariantList = (variants = [], productId, productHandle) => {
  toArray(variants).forEach((variant) => {
    const variantId = variant.id ?? variant.variantId
    const variantProductId = variant.productId || variant.product_id || productId
    addVariantMapping(variantId, variantProductId, productHandle)
  })
}

const collectVariantMapData = () => {
  if (variantMapInitialised) return

  if (typeof window === 'undefined') return

  const { theme } = window
  if (!theme) return

  if (theme.product) {
    collectFromProduct(theme.product)
  }

  toArray(theme.combinedListingsProductVariants).forEach((variantProduct) => {
    collectFromProduct(variantProduct)
  })

  toArray(theme.collection?.products).forEach((product) => {
    collectFromProduct(product)
  })

  toArray(theme.flavourCollections).forEach((collection) => {
    toArray(collection.products).forEach((product) => {
      collectFromProduct(product)
    })
  })

  const freeGiftProducts = theme.cart?.freeGift?.collectionProducts
  toArray(freeGiftProducts).forEach((product) => {
    const productId = product.productId || product.id
    const handle = product.handle
    collectFromVariantList(product.variants, productId, handle)
  })

  const customsCharge = theme.cart?.customsCharge
  if (customsCharge?.variantId) {
    addVariantMapping(customsCharge.variantId, customsCharge.productId, customsCharge.handle)
  }

  const popupProducts = theme.upsellPopups?.general?.collectionProducts
  toArray(popupProducts).forEach((product) => {
    const productId = product.productId || product.id
    const handle = product.handle
    collectFromVariantList(product.variants, productId, handle)
  })

  const minicartProducts = theme.upsellMinicart?.collectionProducts
  toArray(minicartProducts).forEach((item) => {
    const handle = item.handle || extractHandleFromUrl(item.url)
    addVariantMapping(item.id, item.productId, handle)
  })

  const bundleEntries = theme.bundles
  toArray(bundleEntries).forEach((bundle) => {
    if (bundle?.product?.variantId) {
      addVariantMapping(bundle.product.variantId, bundle.product.productId, bundle.product.handle)
    }
    toArray(bundle?.collections).forEach((collection) => {
      toArray(collection.products).forEach((product) => {
        addVariantMapping(product.variantId, product.productId, product.handle)
      })
    })
  })

  const productBundle = theme.bundle
  if (productBundle?.product?.variantId) {
    addVariantMapping(productBundle.product.variantId, productBundle.product.productId, productBundle.product.handle)
  }
  toArray(productBundle?.collections).forEach((collection) => {
    toArray(collection.products).forEach((product) => {
      addVariantMapping(product.variantId, product.productId, product.handle)
    })
  })

  variantMapInitialised = true
}

const detectWebView = () => {
  if (cachedWebViewDetection === true) return true

  if (typeof window === 'undefined') {
    return false
  }

  let detected = false

  try {
    const params = new URLSearchParams(window.location.search)
    detected = params.get(SALES_CHANNEL_PARAM) === SALES_CHANNEL_VALUE
  } catch (error) {
    detected = false
  }

  if (!detected) {
    detected = document?.documentElement?.classList?.contains('venn-app-webview') || false
  }

  if (!detected && typeof document !== 'undefined') {
    const cookieMatch = document.cookie.match(new RegExp(`(?:^|; )${SALES_CHANNEL_COOKIE}=([^;]*)`))
    if (cookieMatch) {
      detected = cookieMatch[1] === 'true'
    }
  }

  if (detected) {
    cachedWebViewDetection = true
  }

  return detected
}

const postMessageToNative = (payload) => {
  if (!payload) return false

  if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.addCartLine) {
    try {
      window.webkit.messageHandlers.addCartLine.postMessage(payload)
      return true
    } catch (error) {
      console.warn('Failed to post addCartLine message via webkit handler', error)
    }
  }

  if (window.JavaInterface && typeof window.JavaInterface.addCartLine === 'function') {
    try {
      window.JavaInterface.addCartLine(JSON.stringify(payload))
      return true
    } catch (error) {
      console.warn('Failed to post addCartLine message via Java interface', error)
    }
  }

  return false
}

const buildLineItemPayload = (item = {}) => {
  const variantId = item.id ?? item.variantId
  if (!variantId && variantId !== 0) return null

  collectVariantMapData()
  const mapping = variantProductMap.get(String(variantId)) || {}
  const merchandiseId = mapping.merchandiseId || toGid('ProductVariant', variantId)
  const productId = mapping.productId || null

  const linePayload = {
    merchandiseId,
    productId,
    quantity: Number(item.quantity ?? 1)
  }

  const attributes = normaliseProperties(item.properties || item.attributes)
  if (attributes && Object.keys(attributes).length) {
    linePayload.attributes = attributes
  }

  const sellingPlanId = item.selling_plan ?? item.selling_plan_id ?? item.sellingPlanId
  if (sellingPlanId) {
    linePayload.sellingPlanId = toGid('SellingPlan', sellingPlanId)
  }

  return linePayload
}

export const notifyNativeAppOfAddToCart = (items) => {
  if (!detectWebView()) return

  const lineItems = toArray(items)
  lineItems.forEach((item) => {
    const payload = buildLineItemPayload(item)
    if (payload) {
      postMessageToNative(payload)
    }
  })
}

export const notifyNativeAppOfCustomsChargeLine = ({ variantId, productId, quantity }) => {
  if (!detectWebView()) return
  if (quantity <= 0) return

  if (productId) {
    addVariantMapping(variantId, productId, null)
  }

  const payload = buildLineItemPayload({ id: variantId, quantity })
  if (!payload) return

  postMessageToNative(payload)
}

export const isVennAppsWebView = () => detectWebView()
